import {
  Button,
  Chip,
  Division,
  Drawer,
  Flex,
  Heading,
  Icon,
  Icons,
  Menu,
  MenuItemProps,
  Span,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  useToast,
} from "@ir/ui";
import { PromoStatusTransitionType } from "@/core/enums/PromoStatusTransitionType";
import { WorkspacePermissions } from "@/core/permissions/Workspace.permissions";
import { useConfirm, useElementHeight, useKeyboard, useUserPermissions, useWorkspace } from "@/hooks";
import HasAnyPermission from "@/presentation/workspace/HasAnyPermission";
import { Activity, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PromoProgramEditMode } from "@/store/promotion";
import {
  Direction,
  usePromotionAppliedDeductions,
  usePromotionDrawer,
  usePromotionEdit,
  usePromotionNavigation,
  usePromotionProduct,
  usePromotions,
} from "../hooks";
import { formatCreatedRelative } from "../utils/promotionDisplay.utils";
import { PromoStatusBadge } from "./PromoStatusBadge";
import ProgramInfoTab from "./ProgramInfoTab";
import "../styles/TradePromotion.scss";
import ProgressBar from "@/presentation/deduction/components/DrawerProgressBar";
import ProductsInScopeTab from "./ProductsInScopeTab";
import AppliedDeductionsTab from "./AppliedDeductionsTab";
import PromotionDocumentButton from "./PromotionDocumentButton";
import PromotionDocumentViewer from "./PromotionDocumentViewer";

const STATUS_TRANSITION_SUCCESS_MESSAGES: Record<PromoStatusTransitionType, string> = {
  [PromoStatusTransitionType.ACTIVATE]: "Promotion activated",
  [PromoStatusTransitionType.RE_ACTIVATE]: "Promotion reactivated",
  [PromoStatusTransitionType.MARK_COMPLETED]: "Promotion marked as completed",
  [PromoStatusTransitionType.CANCEL]: "Promotion paused",
  [PromoStatusTransitionType.DELETE]: "Promotion deleted",
};

const PromotionDrawer = memo(() => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [headerContentRef, headerContentHeight] = useElementHeight<HTMLDivElement>();
  const [tabHeaderRef, tabHeaderHeight] = useElementHeight<HTMLDivElement>();
  const {
    promotionDrawerInfo,
    setPromotionDrawerInfo,
    promoDetail,
    isPromoDetailLoading,
    isPromoDetailTransitioning,
    getPromoDetail,
    putPromoStatus,
    resetPromoDetail,
    isFirstItem,
    isLastItem,
    isNextPrevEnabled,
    setIsNextPrevEnabled,
  } = usePromotionDrawer();
  const { getPromotions } = usePromotions();
  const { editMode, setEditMode, isPromoProgramUpdating } = usePromotionEdit();
  const { handlePromotionNavigation: promotionNavigation } = usePromotionNavigation();
  const { setCurrentPage: setPromoProductsCurrentPage } = usePromotionProduct();
  const {
    setCurrentPage: setAppliedDeductionsCurrentPage,
    resetAppliedDeductions,
    getPromoDeductionApplySummary,
  } = usePromotionAppliedDeductions();
  const { settings } = useWorkspace();
  const { hasAnyPermission } = useUserPermissions();
  const confirm = useConfirm();
  const toast = useToast();
  const [selectedTab, setSelectedTab] = useState<string>("info");
  const [isPromotionDocumentViewerOpen, setIsPromotionDocumentViewerOpen] = useState(false);

  const previousButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const [lastNavigationDirection, setLastNavigationDirection] = useState<Direction | null>(null);

  const canEdit = hasAnyPermission([WorkspacePermissions.TRADE_PROMOTIONS_EDIT]);
  const canDelete = hasAnyPermission([WorkspacePermissions.TRADE_PROMOTIONS_DELETE]);
  const isEditingDetails = editMode === PromoProgramEditMode.DETAILS;
  const isActionsDisabled = isPromoDetailLoading || isPromoDetailTransitioning || isPromoProgramUpdating;

  const { promoItem, promoEventId, isPromoDrawerOpen, nextPrevEnabled } = promotionDrawerInfo;
  const dateFormat = settings?.displayDateFormat ?? "MMM D, YYYY";
  const numberFormat = settings?.displayNumberFormat ?? "0,0.00";
  const isPromotionDocumentViewerVisible = isPromotionDocumentViewerOpen && promoDetail?.sourceDocument !== null;
  const sourcePromotionDocument = promoDetail?.sourceDocument ?? null;

  const handleCloseDocumentViewer = useCallback(() => {
    setIsPromotionDocumentViewerOpen(false);
  }, []);

  const handleToggleDocumentViewer = useCallback(() => {
    setIsPromotionDocumentViewerOpen(isOpen => !isOpen);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsPromotionDocumentViewerOpen(false);
    setPromotionDrawerInfo({ ...promotionDrawerInfo, isPromoDrawerOpen: false });
    resetPromoDetail();
    resetAppliedDeductions();
    setSelectedTab("info");
  }, [promotionDrawerInfo, resetPromoDetail, resetAppliedDeductions, setPromotionDrawerInfo]);

  const handlePromotionNavigation = useCallback(
    async (direction: Direction) => {
      if (isEditingDetails) {
        const confirmResult = await confirm({
          title: "Unsaved Changes",
          message: "You have unsaved updates. Leave without saving?",
          confirmText: "Discard",
          cancelText: "Go Back",
        });
        if (!confirmResult) return;
      }
      setIsPromotionDocumentViewerOpen(false);
      setEditMode(null);
      promotionNavigation(direction);
      setLastNavigationDirection(direction);
      setIsNextPrevEnabled(true);
    },
    [isEditingDetails, confirm, setEditMode, promotionNavigation, setIsNextPrevEnabled]
  );

  useEffect(() => {
    if (!isPromoDetailLoading && lastNavigationDirection && isNextPrevEnabled) {
      if (lastNavigationDirection === Direction.NEXT) {
        nextButtonRef.current?.focus();
      } else if (lastNavigationDirection === Direction.PREVIOUS) {
        previousButtonRef.current?.focus();
      }
    }
  }, [isPromoDetailLoading, lastNavigationDirection, isNextPrevEnabled]);

  useEffect(() => {
    setEditMode(null);
  }, [promoEventId, setEditMode]);

  useKeyboard({
    keys: ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Escape"],
    onKeyDown: e => {
      if (!isPromoDrawerOpen) return;
      if (e.key === "Escape") {
        handleCloseDrawer();
        return;
      }
      if (!isNextPrevEnabled || isActionsDisabled) return;
      const isNext = e.key === "ArrowDown" || e.key === "ArrowRight";
      if (isNext) {
        void handlePromotionNavigation(Direction.NEXT);
      } else {
        void handlePromotionNavigation(Direction.PREVIOUS);
      }
    },
  });

  useEffect(() => {
    if (isPromoDrawerOpen && promoEventId) {
      setPromoProductsCurrentPage(1);
      setAppliedDeductionsCurrentPage(1);
      getPromoDetail();
    }
  }, [isPromoDrawerOpen, promoEventId, getPromoDetail, setPromoProductsCurrentPage, setAppliedDeductionsCurrentPage]);

  const headerTitle = promoDetail?.promoName ?? promoItem?.promoName ?? "";
  const headerCode = promoDetail?.promoCode ?? "";
  const headerStatus = promoDetail?.status ?? promoItem?.status;
  const createdByName = promoDetail?.lifecycle.createdByName;
  const createdAt = promoDetail?.lifecycle.createdAt;
  const createdMeta =
    createdByName && createdAt
      ? `Created by ${createdByName} · ${formatCreatedRelative(createdAt)}`
      : createdByName
        ? `Created by ${createdByName}`
        : null;

  const primaryActionLabel = promoDetail?.primaryActionLabel() ?? null;

  const menuItems = useMemo<MenuItemProps[]>(() => {
    const items: MenuItemProps[] = [];
    if (canEdit && promoDetail?.canCancelProgram()) {
      items.push({ key: "cancel", label: "Pause Promotion" });
    }
    if (canDelete && promoDetail?.canDeleteProgram()) {
      items.push({ key: "delete", label: "Delete Promotion" });
    }
    return items;
  }, [promoDetail, canEdit, canDelete]);

  const refreshPromotionsList = useCallback(() => {
    void getPromotions({ isLoading: false, isRefreshing: true });
  }, [getPromotions]);

  const getConfirmation = useCallback(
    (type: PromoStatusTransitionType) => {
      const programName = promoDetail?.promoName ?? promoItem?.promoName ?? "";
      const retailerName = promoDetail?.retailer?.retailerName ?? "the retailer";

      if (type === PromoStatusTransitionType.RE_ACTIVATE) {
        return {
          title: `Re-Activate "${programName}"?`,
          message:
            "Reopens the promotion for new matching. Deductions received while paused stay as-is (unmatched or manually linked) - no retroactive re-matching.",
          confirmText: "Re-Activate Promotion",
          cancelText: "Go Back",
        };
      } else if (type === PromoStatusTransitionType.CANCEL) {
        return {
          title: `Pause "${programName}"?`,
          message:
            "Stops new matches immediately. Already-confirmed deductions keep their status. Re-activate later if needed.",
          confirmText: "Pause Promotion",
          cancelText: "Go Back",
        };
      } else if (type === PromoStatusTransitionType.DELETE) {
        return {
          title: `Delete Promotion "${programName}"?`,
          message: "This promotion will be permanently removed from the listing. This action cannot be undone.",
          confirmText: "Delete",
          cancelText: "Go Back",
        };
      } else if (type === PromoStatusTransitionType.ACTIVATE) {
        return {
          title: `Activate "${programName}"?`,
          message: `Starting now, incoming trade deductions from ${retailerName} will be checked against this promotion.`,
          confirmText: "Activate Promotion",
          cancelText: "Go Back",
        };
      } else if (type === PromoStatusTransitionType.MARK_COMPLETED) {
        return {
          title: `Mark "${programName}" as Completed?`,
          message:
            "Use once the promotional window ends. New deductions stop auto-matching; confirmed matches and reporting history stay intact.",
          confirmText: "Mark as Completed",
          cancelText: "Go Back",
        };
      }
    },
    [promoDetail?.promoName, promoDetail?.retailer?.retailerName, promoItem?.promoName]
  );

  const handleStatusTransition = useCallback(
    async (type: PromoStatusTransitionType) => {
      const confirmation = getConfirmation(type);
      if (!confirmation) {
        return;
      }
      const confirmResult = await confirm(confirmation);
      if (!confirmResult) {
        return;
      }

      try {
        const result = await putPromoStatus(type);
        toast.success({ content: STATUS_TRANSITION_SUCCESS_MESSAGES[type] });
        if (result.type === PromoStatusTransitionType.DELETE) {
          handleCloseDrawer();
        }
        if (result.type === PromoStatusTransitionType.CANCEL) {
          resetAppliedDeductions();
          setAppliedDeductionsCurrentPage(1);
          void getPromoDeductionApplySummary({
            promoEventId: result.detail.promoEventId,
            isLoading: false,
            isRefreshing: true,
          });
        }
        refreshPromotionsList();
      } catch (error) {
        toast.error({
          content: error instanceof Error ? error.message : "Failed to update promotion status",
        });
      }
    },
    [
      getConfirmation,
      confirm,
      putPromoStatus,
      toast,
      handleCloseDrawer,
      refreshPromotionsList,
      resetAppliedDeductions,
      setAppliedDeductionsCurrentPage,
      getPromoDeductionApplySummary,
    ]
  );

  const handlePrimaryAction = useCallback(async () => {
    if (!promoDetail) {
      return;
    }
    const type = promoDetail.isCancelled()
      ? PromoStatusTransitionType.RE_ACTIVATE
      : promoDetail.isDraft()
        ? PromoStatusTransitionType.ACTIVATE
        : PromoStatusTransitionType.MARK_COMPLETED;
    await handleStatusTransition(type);
  }, [promoDetail, handleStatusTransition]);

  const handleMenuSelect = useCallback(
    async (key: string) => {
      if (key === "cancel") {
        await handleStatusTransition(PromoStatusTransitionType.CANCEL);
        return;
      }

      if (key === "delete") {
        await handleStatusTransition(PromoStatusTransitionType.DELETE);
      }
    },
    [handleStatusTransition]
  );

  const productsCount = promoDetail?.productsInScopeCount ?? 0;
  const appliedCount = promoDetail?.appliedDeductionsCount ?? 0;

  const renderHeader = () => (
    <Flex
      className="promotion-drawer-header bg-white"
      ref={headerContentRef}
      style={{ marginLeft: isPromotionDocumentViewerVisible ? "50%" : "0" }}
    >
      {isActionsDisabled && <ProgressBar />}
      <Flex
        direction="row"
        align="start"
        justifyContent="space-between"
        wrap="no-wrap"
        className="px-4 py-3 w-100"
      >
        <Flex direction="column" className="promotion-drawer-header-titles min-w-0" style={{ width: "calc(100% - 45px)" }}>
          <Heading level={6} className="text-grey-700 font-bold m-0" htmlTitle={headerTitle}>
            {headerTitle}{" "}
            {headerStatus && (
              <PromoStatusBadge statusName={headerStatus.promoStatusName} statusKey={headerStatus.promoStatusKey} />
            )}
          </Heading>
          <Flex direction="row" align="center" gap={0.75} className="mt-1 flex-wrap">
            {headerCode && <Span className="text-grey-500 text-sm">{headerCode}</Span>}
          </Flex>
          {createdMeta && <Span className="text-grey-500 text-xs mt-1">{createdMeta}</Span>}
        </Flex>
        <Flex align="center" justifyContent="end" wrap="no-wrap">
          <Activity mode={nextPrevEnabled ? "visible" : "hidden"}>
            <Button
              ref={previousButtonRef}
              size="medium"
              variant="clear"
              color="grey"
              className={(isFirstItem || isActionsDisabled) ? "text-grey-200" : "text-grey-500"}
              onPress={() => void handlePromotionNavigation(Direction.PREVIOUS)}
              isDisabled={isFirstItem || isActionsDisabled}
              aria-label="Previous promotion"
            >
              <Icon name={Icons.ChevronLeft} size="medium" />
            </Button>
            <Button
              ref={nextButtonRef}
              size="medium"
              variant="clear"
              color="grey"
              className={(isLastItem || isActionsDisabled) ? "text-grey-200" : "text-grey-500"}
              onPress={() => void handlePromotionNavigation(Direction.NEXT)}
              isDisabled={isLastItem || isActionsDisabled}
              aria-label="Next promotion"
            >
              <Icon name={Icons.ChevronRight} size="medium" />
            </Button>
          </Activity>
          <Button size="medium" variant="clear" color="grey" onPress={handleCloseDrawer} aria-label="Close drawer">
            <Icon name={Icons.Close} size="medium" />
          </Button>
        </Flex>
      </Flex>

      <Flex align="center" justifyContent="space-between" className="px-4 pb-3 promotion-drawer-actions w-100">
        <Flex align="stretch">
          <PromotionDocumentButton
            document={sourcePromotionDocument}
            isOpen={isPromotionDocumentViewerVisible}
            isDisabled={isActionsDisabled}
            onPress={handleToggleDocumentViewer}
          />
          {promoDetail?.canEditProgramDetails() && (
            <HasAnyPermission requiredPermissions={[WorkspacePermissions.TRADE_PROMOTIONS_EDIT]}>
              <Button
                size="small"
                variant="outline"
                color="grey"
                className="py-1"
                isDisabled={isActionsDisabled || isEditingDetails}
                onPress={() => setEditMode(PromoProgramEditMode.DETAILS)}
              >
                <Icon name={Icons.Edit} slot="start" size="small" />
                Edit Promotion
              </Button>
            </HasAnyPermission>
          )}
          {menuItems.length > 0 && (
            <Menu items={menuItems} onSelect={handleMenuSelect} placement="bottom start">
              <Button
                size="small"
                variant="outline"
                color="grey"
                aria-label="More actions"
                className="py-1"
                isDisabled={isActionsDisabled}
              >
                <Icon name={Icons.DotsVertical} size="small" />
              </Button>
            </Menu>
          )}
        </Flex>
        {primaryActionLabel && (
          <HasAnyPermission requiredPermissions={[WorkspacePermissions.TRADE_PROMOTIONS_EDIT]}>
            <Button
              size="small"
              variant="solid"
              color="primary"
              isDisabled={isActionsDisabled}
              onPress={handlePrimaryAction}
            >
              {primaryActionLabel}
            </Button>
          </HasAnyPermission>
        )}
      </Flex>
    </Flex>
  );

  return (
    <Drawer
      key="promotion-drawer"
      className="promotion-drawer"
      isOpen={isPromoDrawerOpen ?? false}
      mask={false}
      size="large"
      width={isPromotionDocumentViewerVisible ? "100%" : undefined}
      isKeyboardDismissDisabled={true}
      header={renderHeader()}
    >
      <Flex direction="row" align="stretch" justifyContent="end">
        {isPromotionDocumentViewerVisible && sourcePromotionDocument && (
          <PromotionDocumentViewer
            document={sourcePromotionDocument}
            onClose={handleCloseDocumentViewer}
            className="open-promotion-document-viewer"
          />
        )}
        <Tabs
          orientation="horizontal"
          aria-label="Promotion Drawer Tabs"
          keyboardActivation="manual"
          selectedKey={selectedTab}
          onSelectionChange={key => setSelectedTab(String(key))}
          style={{ height: "100%", width: isPromotionDocumentViewerVisible ? "50%" : "100%" }}
        >
          <Division className="bg-white border-bottom-1 border-color-grey-50 px-4" ref={tabHeaderRef}>
            <Flex justifyContent="space-between" align="center" style={{ marginBottom: "-1px" }}>
              <TabList>
                <Tab id="info" title="Promotion Info">
                  Promotion Info
                </Tab>
                <Tab id="products" title="Products in Scope">
                  <Flex align="center" gap={0.5}>
                    Products in Scope
                    <Chip borderless size="small" color="grey" label={isPromoDetailLoading ? "-" : String(productsCount)} />
                  </Flex>
                </Tab>
                <Tab id="deductions" title="Applied Deductions">
                  <Flex align="center" gap={0.5}>
                    Applied Deductions
                    <Chip borderless size="small" color="grey" label={isPromoDetailLoading ? "-" : String(appliedCount)} />
                  </Flex>
                </Tab>
              </TabList>
            </Flex>
          </Division>
          <Division ref={contentRef} className="flex flex-1 flex-align-start promotion-drawer-body">
            <TabPanel
              id="info"
              role="tabpanel"
              aria-label="Promotion Info"
              className="flex-1 w-100 p-4"
              style={{ height: `calc(100vh - ${headerContentHeight + tabHeaderHeight}px)`, overflow: "auto" }}
            >
              <ProgramInfoTab promoDetail={promoDetail} dateFormat={dateFormat} numberFormat={numberFormat} isLoading={isPromoDetailLoading} />
            </TabPanel>
            <TabPanel
              id="products"
              role="tabpanel"
              aria-label="Products in Scope"
              className="flex-1 w-100 p-4 d-flex flex-column"
              style={{ height: `calc(100vh - ${headerContentHeight + tabHeaderHeight}px)`, overflow: "hidden" }}
            >
              <ProductsInScopeTab promoDetail={promoDetail} />
            </TabPanel>
            <TabPanel
              id="deductions"
              role="tabpanel"
              aria-label="Applied Deductions"
              className="flex-1 w-100 p-4 d-flex flex-column"
              style={{ height: `calc(100vh - ${headerContentHeight + tabHeaderHeight}px)`, overflow: "hidden" }}
            >
              <AppliedDeductionsTab promoDetail={promoDetail} />
            </TabPanel>
          </Division>
        </Tabs>
      </Flex>
    </Drawer>
  );
});

PromotionDrawer.displayName = "PromotionDrawer";

export default PromotionDrawer;
